import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Calendar, Clock, Send, Phone, Video } from "lucide-react";
import { siteInfo } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const AppointmentPage = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "",
    date: "", time: "", meetingType: "in-person",
    purpose: "", message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: "", email: "", phone: "", company: "", date: "", time: "", meetingType: "in-person", purpose: "", message: "" });
  };

  const meetingTypes = [
    { value: "in-person", label: "In-Person Visit", Icon: Calendar },
    { value: "phone", label: "Phone Call", Icon: Phone },
    { value: "virtual", label: "Virtual Meeting", Icon: Video }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-black/40" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Make an Appointment
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Schedule a meeting with our team
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Form */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[800px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="bg-white p-8 md:p-12">
              <div className="text-center mb-10">
                <h2 className="text-3xl text-[#1a1a1a] font-light"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Book Your Visit
                </h2>
                <p className="mt-3 text-[#888] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Or call us directly at{" "}
                  <a href={`tel:${siteInfo.phone}`} className="text-[#c8a96e] hover:underline">
                    {siteInfo.phone}
                  </a>
                </p>
              </div>

              {submitted && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-[#c8a96e]/10 border border-[#c8a96e]/30 text-[#c8a96e] text-sm text-center"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Appointment request received! We'll confirm within 24 hours.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Meeting Type */}
                <div>
                  <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-3"
                    style={{ fontFamily: "'Inter', sans-serif" }}>Meeting Type</label>
                  <div className="grid grid-cols-3 gap-3">
                    {meetingTypes.map((type) => (
                      <button key={type.value} type="button"
                        onClick={() => setFormData({...formData, meetingType: type.value})}
                        className={`flex flex-col items-center gap-2 p-4 border text-[12px] tracking-[0.1em] uppercase transition-all duration-300 ${
                          formData.meetingType === type.value
                            ? "border-[#c8a96e] bg-[#c8a96e]/5 text-[#c8a96e]"
                            : "border-[#eee] text-[#999] hover:border-[#c8a96e]"
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        <type.Icon className="w-5 h-5" />
                        <span className="text-[10px]">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Full Name *</label>
                    <input type="text" required value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Email *</label>
                    <input type="email" required value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Phone *</label>
                    <input type="tel" required value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Company</label>
                    <input type="text" value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Preferred Date *</label>
                    <input type="date" required value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                      style={{ fontFamily: "'Inter', sans-serif" }}>Preferred Time *</label>
                    <input type="time" required value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}>Purpose of Visit *</label>
                  <select required value={formData.purpose}
                    onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                    className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    <option value="">Select purpose...</option>
                    <option value="wholesale">Wholesale Inquiry</option>
                    <option value="custom">Custom Design Project</option>
                    <option value="partnership">Partnership Discussion</option>
                    <option value="product">Product Viewing</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}>Additional Notes</label>
                  <textarea rows={4} value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }} />
                </div>

                <button type="submit"
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#1a1a1a] text-white text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  <Send className="w-4 h-4" />
                  Request Appointment
                </button>
              </form>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default AppointmentPage;
